import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useMemo } from 'react';
import { PaginationLimit } from '../../../../components/PaginationLimit';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import ViewUserDetailsModal from './ViewUserDetailsModal';
import AuditLogModal from './AuditLogModal';
import DeleteConfirmModal from './DeleteConfirmModal';

const ManageUsersListLayer = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [accessFilter, setAccessFilter] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [auditLogModalOpen, setAuditLogModalOpen] = useState(false);
    const [auditLogUser, setAuditLogUser] = useState(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Sample user data matching cmd.md
    const usersData = [
        { id: 1, name: 'OM PRAKASH AGARWAL', email: 'opagarwal@fingertip.co.in', loginId: 'opagarwal@fingertip.co.in', city: '-', lastLogin: '23/01/2026 22:15', createdOn: '-', status: true, userAccess: true },
        { id: 2, name: 'Pankaj Jha', email: 'pankaj_globsyn@yahoo.co.in', loginId: 'pankaj_globsyn@yahoo.co.in', city: '-', lastLogin: '-', createdOn: '-', status: true, userAccess: true },
        { id: 3, name: 'Esha Agarwal', email: 'esha@fingertip.co.in', loginId: 'esha@fingertip.co.in', city: '-', lastLogin: '-', createdOn: '-', status: false, userAccess: false },
        { id: 4, name: 'Ankit Sureka', email: 'ank.sureka@gmail.com', loginId: 'ank.sureka@gmail.com', city: 'Mumbai', lastLogin: '-', createdOn: '-', status: true, userAccess: false },
        { id: 5, name: 'Apratim De', email: 'apratim.de28@gmail.com', loginId: 'apratim.de28@gmail.com', city: '-', lastLogin: '-', createdOn: '-', status: true, userAccess: false },
        { id: 6, name: 'Sonali Sen', email: 'sonali@encoders.co.in', loginId: 'tu54475@gmail.com', city: '-', lastLogin: '24/02/2014 20:46', createdOn: '-', status: true, userAccess: false },
        { id: 7, name: 'radha saraf', email: 'sarafradha@ymail.com', loginId: 'sarafradha@ymail.com', city: '418790', lastLogin: '04/10/2012 03:19', createdOn: '-', status: true, userAccess: false },
        { id: 8, name: 'Nisha Chowdhury', email: 'nisha2003_4u@yahoo.co.in', loginId: 'nisha2003_4u@yahoo.co.in', city: 'Kolkata', lastLogin: '-', createdOn: '-', status: true, userAccess: false },
        { id: 9, name: 'disha chetani', email: 'disha.chetani@gmail.com', loginId: 'disha.chetani@gmail.com', city: '-', lastLogin: '-', createdOn: '-', status: true, userAccess: false },
        { id: 10, name: 'Deepthi Nambiar', email: 'deeps1256@gmail.com', loginId: 'deeps1256@gmail.com', city: '418790', lastLogin: '12/09/2019 17:22', createdOn: '-', status: true, userAccess: false },
    ];

    // Filtering logic - matches SocietyManagement search pattern
    const filteredUsers = useMemo(() => {
        return usersData.filter(user => {
            const searchMatch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.loginId.toLowerCase().includes(searchTerm.toLowerCase());
            const statusMatch = !statusFilter || (statusFilter === 'active' ? user.status : !user.status);
            const accessMatch = !accessFilter || (accessFilter === 'blocked' ? user.userAccess : !user.userAccess);

            return searchMatch && statusMatch && accessMatch;
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm, statusFilter, accessFilter]);

    const displayUsers = filteredUsers;

    // Modal handlers
    const handleViewClick = (user) => {
        setSelectedUser(user);
        setViewModalOpen(true);
    };

    const handleCloseViewModal = () => {
        setViewModalOpen(false);
        setSelectedUser(null);
    };

    const handleAuditLogClick = (user) => {
        setAuditLogUser(user);
        setAuditLogModalOpen(true);
    };

    const handleCloseAuditLogModal = () => {
        setAuditLogModalOpen(false);
        setAuditLogUser(null);
    };

    const handleDeleteClick = (user) => {
        setUserToDelete(user);
        setDeleteModalOpen(true);
    };

    const handleCloseDeleteModal = () => {
        setDeleteModalOpen(false);
        setUserToDelete(null);
    };

    const handleConfirmDelete = async () => {
        setIsDeleting(true);
        try {
            // TODO: Call API to delete user
            console.log('Deleting user:', userToDelete);
            await new Promise(resolve => setTimeout(resolve, 1000));
            handleCloseDeleteModal();
        } catch (error) {
            console.error('Error deleting user:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleStatusChange = (user) => {
        // TODO: Call API to update user status
        console.log('Toggling status for user:', user);
    };

    const handleUserAccessChange = (user) => {
        // TODO: Call API to update user access
        console.log('Toggling user access for:', user);
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setStatusFilter('');
        setAccessFilter('');
        setFromDate('');
        setToDate('');
    };

    return (
        <div className="card">
            {/* Filter Section - SocietyManagement Style */}
            <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div className="d-flex flex-wrap align-items-center gap-3">
                    <div className="icon-field">
                        <input
                            type="text"
                            className="form-control form-control-sm w-auto"
                            placeholder="Search"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <span className="icon">
                            <Icon icon="ion:search-outline" />
                        </span>
                    </div>

                    <div className="d-flex align-items-center gap-2">
                        <span>From:</span>
                        <input
                            type="date"
                            className="form-control"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                        />
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <span>To:</span>
                        <input
                            type="date"
                            className="form-control"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                        />
                    </div>
                </div>

                <div className="d-flex flex-wrap align-items-center gap-3">
                    <div className="tw-w-40">
                        <SearchableDropdown
                            value={statusFilter || 'all'}
                            onChange={(v) => setStatusFilter(v === 'all' ? '' : v)}
                            options={[
                                { value: 'all', label: 'All Status' },
                                { value: 'active', label: 'Active' },
                                { value: 'inactive', label: 'Inactive' },
                            ]}
                            placeholder="Status"
                            clearable={false}
                            small
                        />
                    </div>
                    <div className="tw-w-40">
                        <SearchableDropdown
                            value={accessFilter || 'all'}
                            onChange={(v) => setAccessFilter(v === 'all' ? '' : v)}
                            options={[
                                { value: 'all', label: 'All Access' },
                                { value: 'blocked', label: 'Blocked' },
                                { value: 'unblocked', label: 'Unblocked' },
                            ]}
                            placeholder="Access"
                            clearable={false}
                            small
                        />
                    </div>
                    <button
                        onClick={handleClearFilters}
                        className="tw-h-8 tw-inline-flex tw-items-center tw-px-3 tw-rounded-md tw-text-xs tw-font-medium tw-text-gray-600 tw-bg-white tw-border tw-border-gray-200 hover:tw-bg-gray-50"
                    >
                        Clear
                    </button>
                </div>
            </div>

            {/* Table Section - Bootstrap Table Matching SocietyManagement */}
            <div className="card-body">
                <table className="table bordered-table mb-0">
                    <thead>
                        <tr>
                            <th scope="col">
                                <div className="form-check style-check d-flex align-items-center">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="checkAll"
                                    />
                                    <label className="form-check-label" htmlFor="checkAll">
                                        #
                                    </label>
                                </div>
                            </th>
                            <th scope="col">Name</th>
                            <th scope="col">Email</th>
                            <th scope="col">Login ID</th>
                            <th scope="col">City</th>
                            <th scope="col">Last Login</th>
                            <th scope="col">Status</th>
                            <th scope="col">User Access</th>
                            <th scope="col">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {displayUsers.map((user, index) => (
                            <tr key={user.id}>
                                <td>
                                    <div className="form-check style-check d-flex align-items-center">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id={`check${user.id}`}
                                        />
                                        <label className="form-check-label" htmlFor={`check${user.id}`}>
                                            {index + 1}
                                        </label>
                                    </div>
                                </td>
                                <td>
                                    <h6 className="text-md mb-0 fw-medium">{user.name}</h6>
                                </td>
                                <td>{user.email}</td>
                                <td>{user.loginId}</td>
                                <td>{user.city}</td>
                                <td>
                                    <span className="text-xs text-gray-600">{user.lastLogin}</span>
                                </td>
                                <td>
                                    <Switch
                                        checked={user.status}
                                        onChange={() => handleStatusChange(user)}
                                        size="small"
                                    />
                                </td>
                                <td>
                                    <Switch
                                        checked={user.userAccess}
                                        onChange={() => handleUserAccessChange(user)}
                                        size="small"
                                    />
                                </td>
                                <td>
                                    <Tooltip title="View" placement="top" arrow>
                                        <button
                                            onClick={() => handleViewClick(user)}
                                            className="w-32-px h-32-px me-8 bg-primary-light text-primary-600 rounded-circle d-inline-flex align-items-center justify-content-center border-0"
                                        >
                                            <Icon icon="iconamoon:eye-light" />
                                        </button>
                                    </Tooltip>

                                    <Tooltip title="Audit Log" placement="top" arrow>
                                        <button
                                            onClick={() => handleAuditLogClick(user)}
                                            className="w-32-px h-32-px me-8 bg-info-focus text-info-main rounded-circle d-inline-flex align-items-center justify-content-center border-0"
                                        >
                                            <Icon icon="solar:clipboard-list-bold" />
                                        </button>
                                    </Tooltip>

                                    <Tooltip title="Delete" placement="top" arrow>
                                        <button
                                            onClick={() => handleDeleteClick(user)}
                                            className="w-32-px h-32-px me-8 bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center border-0"
                                        >
                                            <Icon icon="mingcute:delete-2-line" />
                                        </button>
                                    </Tooltip>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 pt-24 border-top">
                <span className="text-gray-600">Showing {displayUsers.length} users</span>
                <PaginationLimit />
            </div>

            {/* Modals */}
            <ViewUserDetailsModal
                open={viewModalOpen}
                handleClose={handleCloseViewModal}
                user={selectedUser}
            />
            <AuditLogModal
                open={auditLogModalOpen}
                handleClose={handleCloseAuditLogModal}
                user={auditLogUser}
            />
            <DeleteConfirmModal
                open={deleteModalOpen}
                handleClose={handleCloseDeleteModal}
                handleConfirm={handleConfirmDelete}
                user={userToDelete}
                isLoading={isDeleting}
            />
        </div>
    );
};

export default ManageUsersListLayer;
